import { NodeSelection } from '@tiptap/pm/state';
import { Fragment } from '@tiptap/pm/model';

export function contentBlockCanAppend(parent, node) {
  return !parent.isLeaf && parent.canReplace(parent.childCount, parent.childCount, Fragment.from(node));
}

export function contentBlockCanInsertAt(document, position, node) {
  try {
    const target = document.resolve(position);
    return target.parent.canReplace(target.index(), target.index(), Fragment.from(node));
  } catch { return false; }
}

const PRIVATE_ATTRIBUTES = new Set(['builderId', 'lockId', 'blockLock', 'aiListingSlotId', 'aiListingInstruction', 'aiInstruction', 'aiSlotId', 'aiSlotVersion', 'aiPlaceholder', 'builderPlaceholder']);

export function sanitizeContentClipboardNode(node) {
  const copy = JSON.parse(JSON.stringify(node));
  const visit = (item) => {
    for (const key of Object.keys(item.attrs || {})) if (PRIVATE_ATTRIBUTES.has(key)) delete item.attrs[key];
    for (const child of item.content || []) visit(child);
  };
  visit(copy);
  return copy;
}

/** Structural actions resolve the current node, then pass through the policy plugin. */
export function runContentBlockAction(editor, position, action) {
  if (!editor?.isEditable || typeof position !== 'number') return false;
  const original = editor.state.doc;
  const node = original.nodeAt(position);
  if (!node) return false;
  const resolved = original.resolve(position);
  const index = resolved.index();
  const parent = resolved.parent;
  const transaction = editor.state.tr;
  if (action === 'group') {
    if (node.type.name !== 'container') return false;
    transaction.setNodeMarkup(position, undefined, { ...node.attrs, layout: 'group' });
    transaction.setSelection(NodeSelection.create(transaction.doc, position));
  } else if (action === 'ungroup') {
    if (node.type.name !== 'container' || !node.childCount || !parent.canReplace(index, index + 1, node.content)) return false;
    transaction.replaceWith(position, position + node.nodeSize, node.content);
    transaction.setSelection(NodeSelection.create(transaction.doc, position));
  } else if (action === 'unwrap-columns') {
    if (node.type.name !== 'container' || !node.childCount || !Array.from({ length: node.childCount }, (_, index) => node.child(index)).every((child) => child.type.name === 'column')) return false;
    const content = [];
    node.forEach((column) => column.forEach((child) => content.push(child)));
    const replacement = node.type.create({ ...node.attrs, layout: 'stack', columnCount: null }, content);
    if (!parent.canReplaceWith(index, index + 1, replacement.type)) return false;
    transaction.replaceWith(position, position + node.nodeSize, replacement);
    transaction.setSelection(NodeSelection.create(transaction.doc, position));
  } else if (action === 'duplicate') transaction.insert(position + node.nodeSize, editor.schema.nodeFromJSON(sanitizeContentClipboardNode(node.toJSON())));
  else if (action === 'delete') {
    if (parent.type.name === 'doc' && parent.childCount === 1) transaction.replaceWith(position, position + node.nodeSize, editor.schema.nodes.paragraph.create());
    else transaction.delete(position, position + node.nodeSize);
  } else if (action === 'move-up' && index > 0) {
    const target = position - parent.child(index - 1).nodeSize;
    transaction.delete(position, position + node.nodeSize).insert(target, node);
    transaction.setSelection(NodeSelection.create(transaction.doc, target));
  } else if (action === 'move-down' && index + 1 < parent.childCount) {
    const target = position + parent.child(index + 1).nodeSize;
    transaction.delete(position, position + node.nodeSize).insert(target, node);
    transaction.setSelection(NodeSelection.create(transaction.doc, target));
  } else return false;
  editor.view.dispatch(transaction);
  return editor.state.doc !== original;
}

/** Move one whole block to a document boundary; all profile and lock checks still apply. */
export function moveContentBlockTo(editor, position, target) {
  if (!editor?.isEditable || !Number.isInteger(position) || !Number.isInteger(target)) return false;
  const original = editor.state.doc;
  const node = original.nodeAt(position);
  if (!node?.isBlock || target < 0 || target > original.content.size || (target >= position && target <= position + node.nodeSize)) return false;
  try {
    const transaction = editor.state.tr.delete(position, position + node.nodeSize);
    const destination = transaction.mapping.map(target, -1);
    transaction.insert(destination, node);
    transaction.setSelection(NodeSelection.create(transaction.doc, destination));
    editor.view.dispatch(transaction);
    return editor.state.doc !== original;
  } catch { return false; }
}
